import type { CSSProperties, ReactNode, Ref } from 'react'
import { Fragment } from 'react'

import type { PublicCalendarEntry } from '~/api/types'

import s from './Share.module.css'

/*
 * The line and its stops — the one drawing every multi-gig card is made of.
 *
 * A stop is a ring on the rail with the gig beside it: the name in the reading face, and
 * beside or under it whichever cells of the room, the time and the price that card asks
 * `meta` for, in the mono. The day, when the card shows one, sits to the left of the rail in
 * the mono. Which colour the line is belongs to the card:
 * ice for a week or a room, acid for tonight, grey for the line the announced card draws
 * its new stops on — and a stop can carry its own hue over the line's, which is how the
 * announced card's new ring is acid over its grey rail.
 *
 * Two rows exist, and which one a card draws is which unit its line counts in. `Stop` is a
 * row per gig, which is what a line ordered by time or by price is. `Slot` is a row per day,
 * for a line whose axis is the calendar: it draws one marker however many gigs the day holds
 * and it draws a row for a day that holds none, so the position of a marker down the rail
 * says which day it is.
 */

interface LineProps {
  /** Ice unless told otherwise; tonight's is acid, announced's is grey. */
  hue?: 'ice' | 'acid' | 'grey'
  /** Whether the stops carry a day label left of the rail, which moves the rail right. */
  labelled?: boolean
  className?: string | undefined
  /**
   * Geometry a stylesheet cannot answer for, on the element it belongs to.
   *
   * A card whose row weights are its *payload's* — the week, where a night nobody picked on
   * keeps its place at a share of one that draws something — has as many track lists as there
   * are shapes of week, so no rule can spell them. Everything that is the same on every card
   * of a kind stays in the sheet.
   */
  style?: CSSProperties | undefined
  /**
   * The box itself, for a card that has to know how tall its own rows came out. The week
   * measures this against its last night to decide whether the card has run out of room;
   * nothing else asks, and nothing here reads it.
   */
  ref?: Ref<HTMLDivElement> | undefined
  children: ReactNode
}

export function Line({ hue = 'ice', labelled = false, className, style, ref, children }: LineProps) {
  const tint = hue === 'acid' ? s.acid : hue === 'grey' ? s.grey : ''
  const classes = [s.line, tint, labelled ? s.labelled : '', className ?? '']
  return (
    <div className={classes.filter(Boolean).join(' ')} style={style} ref={ref}>
      <span className={s.rail} aria-hidden="true" />
      {children}
    </div>
  )
}

interface MetaOptions {
  /** Off for the room's own card, where naming it on every stop would say the heading again. */
  venue?: boolean
  /** On for a card whose stops are months apart, where the night is the news. */
  night?: boolean
  /** Off for the same card: a time on a stop months out is noise beside the date. */
  time?: boolean
}

/**
 * `Room · 19:30 · from £23` — whichever of the cells the row has, in one fixed order: the
 * night when asked for, the room, the time, the price. A span per cell rather than one
 * string so the stylesheet can let the room give way to an ellipsis while the time and the
 * price stay whole. The night is the server's `dowShort dayNum monShort`, never a date
 * formatted here, so a 01:00 gig prints the night it belongs to.
 */
export function meta(
  row: PublicCalendarEntry,
  price: string,
  { venue = true, night = false, time = true }: MetaOptions = {},
): ReactNode {
  const when = time && row.timeKnown && row.time ? row.time : ''
  const cells: [string, string][] = []
  if (night) cells.push([s.night, `${row.dowShort} ${row.dayNum} ${row.monShort}`])
  if (venue) cells.push([s.venue, row.venue])
  if (when) cells.push([s.when, when])
  if (price) cells.push([s.price, price])
  return cells.map(([cell, text], i) => (
    <Fragment key={cell}>
      {i > 0 ? <span className={s.sep}> · </span> : null}
      <span className={cell}>{text}</span>
    </Fragment>
  ))
}

interface StopProps {
  /** The day label, when the line carries one. */
  day?: string | undefined
  /**
   * What the stop stands for on a line that mixes them — `new` for a gig, `old` for a gap of
   * gigs already on the line. Read by the stylesheet, which gives each its hue, and by the
   * tests; the component sets no colour itself.
   */
  stop?: 'new' | 'old' | undefined
  children: ReactNode
}

/** One ring on the rail with whatever the card writes beside it. */
export function Stop({ day, stop, children }: StopProps) {
  return (
    <div className={s.stop} data-stop={stop}>
      <span className={`${s.day} ${s.mono}`}>{day ?? ''}</span>
      <span className={s.dot} aria-hidden="true" />
      <div className={s.text}>{children}</div>
    </div>
  )
}

interface SlotProps {
  /** The night this slot stands for, straight off the payload's own row — `2026-09-26`. */
  night: string
  /** `Sat 26` — from that night's own row where it has one, and spelled from `night` where it
   * has not. Every night carries one, which is what makes the column a date axis rather than
   * a list of the days something happened on. */
  day?: string | undefined
  /**
   * How many picks the night holds. One draws the ring every card draws; more than one draws
   * it stretched into a capsule, which is the map's interchange; none draws no marker, no
   * name and no room line — its day and its place against the rail are the whole of it.
   */
  picks: number
  children?: ReactNode
}

/**
 * One *day* of a line whose rows are days rather than gigs — the week's.
 *
 * It carries `.stop`'s own class because the row is a stop's row: the same three columns off
 * the line's four lengths, the same label in the same column, the same ring centred on the
 * same rail. A second copy of that geometry would drift from it, which is what the four
 * lengths exist to stop. What it adds is `data-night`, and `data-quiet` on the nights that
 * draw nothing else.
 *
 * **`data-quiet` is what the sheet floors a quiet night by, and it is an attribute rather
 * than the absence of children on purpose.** The rule it replaced selected the slot on having
 * no children, which a slot stops doing the moment it is given a label. What follows is not
 * the collapse it looks like: `min-height` falls back to `auto` and the slot takes its label's
 * own line box, so a quiet night grows past the half it is meant to be. An attribute the
 * component sets from `picks` cannot be falsified by what is inside the box.
 *
 * `picks` is a count and not the rows, because what the day writes beside its marker is the
 * card's business: this decides the marker's shape and whether anything is drawn at all.
 */
export function Slot({ night, day, picks, children }: SlotProps) {
  if (picks < 1)
    return (
      <div className={s.stop} data-night={night} data-quiet="">
        <span className={`${s.day} ${s.mono}`}>{day ?? ''}</span>
      </div>
    )
  return (
    <div className={s.stop} data-night={night}>
      <span className={`${s.day} ${s.mono}`}>{day ?? ''}</span>
      <span className={picks > 1 ? s.capsule : s.dot} aria-hidden="true" />
      <div className={s.text}>{children}</div>
    </div>
  )
}
